import React from 'react';
import { ExternalLink, AlertTriangle } from 'lucide-react';
import { SourceBadge } from './SourceBadge';
import type { Source, TrustMetadata } from '@/types/trust';

interface ChatSourcesPanelProps {
  sources: Source[];
  trustMetadata?: TrustMetadata;
  onOpenSource?: (source: Source) => void;
}

/**
 * Inline expandable panel that lists the sources backing an assistant message.
 * Default-collapsed affordance lives in the message footer; this component
 * renders the expanded body. Training-only responses get a visible banner.
 */
export const ChatSourcesPanel: React.FC<ChatSourcesPanelProps> = ({
  sources,
  trustMetadata,
  onOpenSource,
}) => {
  const trainingOnly =
    trustMetadata?.trainingDataUsed &&
    sources.every((s) => s.type === 'llm-training');

  return (
    <div
      className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs max-w-[85%]"
      role="region"
      aria-label="Response sources"
    >
      {trainingOnly && (
        <div className="mb-2 flex items-start gap-2 rounded-sm bg-yellow-50 border border-yellow-200 p-2 text-yellow-900">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" aria-hidden="true" />
          <span>
            This response uses general knowledge — no specific sources in your system.
          </span>
        </div>
      )}

      {sources.length === 0 ? (
        <p className="text-gray-500">No sources were attached to this response.</p>
      ) : (
        <ul className="space-y-2">
          {sources.map((s, i) => (
            <li key={`${s.type}-${s.id}-${i}`} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 min-w-0">
                <SourceBadge source={s} onClick={onOpenSource} />
                {s.type === 'web-url' && (
                  <a
                    href={s.id}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-[11px] text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                    aria-label={`Open source URL: ${s.label || s.id}`}
                  >
                    open <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  </a>
                )}
              </div>
              {s.excerpt && (
                <p className="text-gray-600 leading-snug line-clamp-3 pl-0.5">
                  {s.excerpt}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {trustMetadata && typeof trustMetadata.confidenceScore === 'number' && (
        <div className="mt-2 border-t border-gray-200 pt-2 text-[11px] text-gray-500">
          Confidence: {Math.round(trustMetadata.confidenceScore * 100)}%
        </div>
      )}
    </div>
  );
};

ChatSourcesPanel.displayName = 'ChatSourcesPanel';
